import React, { useEffect } from 'react';
import { useInvoiceStore } from '../stores/invoiceStore'
import { useAuthStore } from '../stores/authStore';
import {
  Download,
  Save,
  Trash2,
  Send,
  Loader2,
  Repeat2,
  History,
  X
} from 'lucide-react'
import { motion } from 'framer-motion';
import { generateInvoicePDF } from '../utils/pdfGenerator';
import { DeleteConfirmDialog } from './DeleteConfirmDialog';
import { InvoiceSaveSuccessDialog } from './InvoiceSaveSuccessDialog';
import { useTranslation } from '../hooks/useTranslation';
import { EmailModal } from './email/EmailModal';
import { HistoryDialog } from './HistoryDialog';
import { LoginRequiredDialog } from './LoginRequiredDialog';
import { useScheduleStore, useScheduleForInvoice } from '../stores/scheduleStore';
import { useInvoiceHasContent } from '../stores/selectors/invoiceSelectors';

export const CommandBar = () => {
  const { t } = useTranslation();
  const { isAuthenticated } = useAuthStore();
  const { saveInvoice, resetCurrentInvoice, deleteInvoice, setEditingSection } = useInvoiceStore()
  const currentInvoice = useInvoiceStore((state) => state.currentInvoice)
  const { fetchScheduleForInvoice } = useScheduleStore();
  const hasContent = useInvoiceHasContent();
  const [isDeleteDialogOpen, setIsDeleteDialogOpen] = React.useState(false);
  const [isSuccessDialogOpen, setIsSuccessDialogOpen] = React.useState(false);
  const [previousStatus, setPreviousStatus] = React.useState(currentInvoice.status);

  React.useEffect(() => {
    if (previousStatus === 'concept' && currentInvoice.status === 'saved') {
      setIsSuccessDialogOpen(true);
    }
    setPreviousStatus(currentInvoice.status);
  }, [currentInvoice.status, previousStatus]);

  const [isPdfLoading, setIsPdfLoading] = React.useState(false);
  const [isEmailModalOpen, setIsEmailModalOpen] = React.useState(false);
  const [isHistoryDialogOpen, setIsHistoryDialogOpen] = React.useState(false);
  const [showEmailLoginDialog, setShowEmailLoginDialog] = React.useState(false);
  
  // Get schedule from the shared cache
  const buttonSchedule = useScheduleForInvoice(currentInvoice.id);

  // Fetch schedule data when currentInvoice changes and has a scheduleId
  useEffect(() => {
    const fetchSchedule = async () => {
      if (currentInvoice.status !== 'concept' && currentInvoice.id && currentInvoice.scheduleId) {
        try {
          await fetchScheduleForInvoice(currentInvoice.id);
        } catch (error) {
          console.error(`Error fetching schedule for invoice ${currentInvoice.id}:`, error);
        }
      }
    };
    
    fetchSchedule();
  }, [currentInvoice.id, currentInvoice.scheduleId, currentInvoice.status, fetchScheduleForInvoice]);

  const handleDownloadPDF = async () => {
    try {
      setIsPdfLoading(true);
      
      // Get the invoice element
      const element = document.querySelector('.invoice-content');
      if (!element) {
        throw new Error('Could not find invoice element');
      }

      // Add print mode class to body
      document.body.classList.add('print-mode');

      // Generate PDF using our utility
      await generateInvoicePDF(element as HTMLElement, currentInvoice, {
        translate: t
      });
    } catch (error) {
      console.error('Error generating PDF:', error);
      alert(t('errors.pdfGenerationFailed'));
    } finally {
      setIsPdfLoading(false);
      document.body.classList.remove('print-mode');
    }
  }

  const handleDelete = React.useCallback(() => {
    if (currentInvoice.invoiceNumber && currentInvoice.id) {
      deleteInvoice(currentInvoice.invoiceNumber);
      resetCurrentInvoice();
    }
    setIsDeleteDialogOpen(false);
  }, [currentInvoice.invoiceNumber, currentInvoice.id, deleteInvoice, resetCurrentInvoice]);

  const handleSend = () => {
    if (!isAuthenticated) {
      setShowEmailLoginDialog(true);
      return;
    }
    
    setIsEmailModalOpen(true);
  }

  const handleScheduleClick = async () => {
    setEditingSection({ type: 'schedule' });
  };

  const handleShowHistory = () => {
    setIsHistoryDialogOpen(true);
  };

  const handleSaveWithSchedule = () => {
    // Save the invoice, which will also save the scheduleId
    saveInvoice();
    
    // If this is a concept invoice with scheduleData, it will be saved to the server
    // when the invoice is saved, so we don't need to do anything special here.
    // The scheduleData will be used to create a real schedule in the backend.
  };

  const isSaved = currentInvoice.status === 'saved';
  const isSent = currentInvoice.status === 'sent';
  const hasScheduleId = !!currentInvoice.scheduleId;

  // Get schedule description for button
  const getScheduleButtonText = () => {
    if (!hasScheduleId && !currentInvoice.scheduleData) return t('schedule.create');
    
    // Use the schedule from the shared cache
    if (buttonSchedule?.type) {
      return t(`schedule.${buttonSchedule.type}`);
    }
    
    // Fallback to generic "Scheduled" text if we don't have the schedule type yet
    return t('schedule.scheduled');
  };

  const getStatusElement = () => {
    if (isSent) {
      return (
        <div className="flex items-center space-x-1.5">
          <Send className="w-4 h-4 text-green-600" />
          <span className="text-sm font-medium text-green-600 backdrop-blur-xl">
            {t(`status.${currentInvoice.status}`)}
          </span>
        </div>
      );
    }
    
    return (
      <span className="text-sm font-medium text-primary-500/90 backdrop-blur-xl">
        {t(`status.${currentInvoice.status}`)}
      </span>
    );
  };

  return (
    <div className="w-full max-w-3xl xl:max-w-5xl xl:w-full mx-auto mb-8" data-pdf-exclude="true">
      <div className="relative mx-auto rounded-[32px] border border-neutral-200/50 bg-neutral-100 p-2 backdrop-blur-lg dark:border-neutral-700 dark:bg-neutral-800/50 md:p-4">
        <div className="rounded-[24px] border border-neutral-200 bg-white dark:border-neutral-700 dark:bg-black">
          <div className="p-3 xs:p-4 flex flex-col space-y-2 xs:space-y-3 md:flex-row md:items-center md:justify-between md:space-y-0">
            <div className="flex items-center space-x-3">
              {getStatusElement()}
            </div>
            
            <div className="flex flex-wrap items-center gap-1.5 xs:gap-2">
              {(isSaved || isSent) && (
                <button
                  onClick={handleScheduleClick}
                  className={`btn-secondary flex items-center gap-0.5 xs:gap-1 md:gap-1.5 border-transparent hover:translate-y-0 py-[0.3rem] xs:py-[0.4rem] mt-[1px] text-xs md:text-sm px-2 xs:px-3 ${
                    hasScheduleId 
                      ? 'bg-primary-50 text-primary-600 hover:bg-primary-100' 
                      : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                  } hover:shadow-none`}
                >
                  <Repeat2 className="w-3.5 h-3.5 md:w-4 md:h-4" />
                  {getScheduleButtonText()}
                </button>
              )}
              {currentInvoice.status === 'concept' && hasContent && (
                <button
                  onClick={resetCurrentInvoice}
                  className="btn-secondary border-transparent bg-gray-100 text-gray-600 hover:bg-gray-200 hover:shadow-none hover:translate-y-0 py-[0.3rem] xs:py-[0.4rem] mt-[1px] text-xs md:text-sm px-2 xs:px-3"
                >
                  <X className="w-3.5 h-3.5 md:w-4 md:h-4" />
                  <span>{t('actions.cancel')}</span>
                </button>
              )}
              <button
                onClick={handleDownloadPDF}
                className="btn-secondary text-xs md:text-sm px-2 xs:px-3 py-1.5 xs:py-2"
                disabled={isPdfLoading}
              >
                {isPdfLoading ? (
                  <>
                    <Loader2 className="w-3.5 h-3.5 md:w-4 md:h-4 animate-spin" />
                    <span>{t('actions.generatingPDF')}</span>
                  </>
                ) : (
                  <>
                    <Download className="w-4 h-4" />
                    <span>{t('actions.exportPDF')}</span>
                  </>
                )}
              </button>
              {isSent ? (
                <motion.button
                  whileHover={{ scale: 1.01 }}
                  whileTap={{ scale: 0.98 }}
                  onClick={handleShowHistory}
                  className="btn-secondary text-xs md:text-sm px-2 xs:px-3 py-1.5 xs:py-2"
                >
                  <History className="w-4 h-4" />
                  <span>{t('email.history')}</span>
                </motion.button>
              ) : isSaved ? (
                <button
                  onClick={handleSend}
                  className="btn-success text-xs md:text-sm px-2 xs:px-3 py-1.5 xs:py-2"
                >
                  <Send className="w-4 h-4" />
                  <span>{t('actions.previewSend')}</span>
                </button>
              ) : (
                <button
                  onClick={handleSaveWithSchedule}
                  className="btn-primary text-xs md:text-sm px-2 xs:px-3 py-1.5 xs:py-2"
                >
                  <Save className="w-4 h-4" />
                  <span>{t('actions.saveInvoice')}</span>
                </button>
              )}
              {(isSaved || isSent) && (
                <button
                  onClick={() => setIsDeleteDialogOpen(true)}
                  className="btn-danger text-xs md:text-sm px-2 xs:px-3 py-1.5 xs:py-2"
                >
                  <Trash2 className="w-4 h-4" />
                  <span>{t('actions.delete')}</span>
                </button>
              )}
            </div>
          </div>
        </div>
      </div>

      <DeleteConfirmDialog
        isOpen={isDeleteDialogOpen}
        onClose={() => setIsDeleteDialogOpen(false)}
        onConfirm={handleDelete}
        titleKey="dialog.deleteInvoice.title"
        messageKey="dialog.deleteInvoice.message"
      />

      <EmailModal 
        isOpen={isEmailModalOpen}
        onClose={() => setIsEmailModalOpen(false)}
      />

      <HistoryDialog
        isOpen={isHistoryDialogOpen}
        onClose={() => setIsHistoryDialogOpen(false)}
      />

      <LoginRequiredDialog
        isOpen={showEmailLoginDialog}
        onClose={() => setShowEmailLoginDialog(false)}
        feature="email"
      />

      <InvoiceSaveSuccessDialog
        isOpen={isSuccessDialogOpen}
        onClose={() => setIsSuccessDialogOpen(false)}
        onNewInvoice={resetCurrentInvoice}
      />
    </div>
  )
}       